import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import * as cee from '../../Common/CeeEnvisionWebComponents';
import { C3Service } from '../../c3.service';
import { FeatureEditorChild } from '../feature-editor/feature-editor.component';

export enum IsoType {
  Isosurface,
  Isovolume,
}

@Component({
    selector: 'app-iso-editor',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './iso-editor.component.html',
    styleUrls: ['./iso-editor.component.css'],
    standalone: false
})
export class IsoEditorComponent extends FeatureEditorChild implements OnInit {
  @Input() public type!: IsoType;
  public title!: string;
  public getIso!: (id: number) => cee.ug.Isosurface | cee.ug.Isovolume;
  public getIsos!: () => cee.ug.ResultInfo[];
  public addIso!: () => number;
  public deleteIso!: (id: number) => void;
  public setDefaultIsoValues!: () => void;

  private _c3Service: C3Service;
  private _isoObject!: cee.ug.Isosurface | cee.ug.Isovolume;
  private _minScalarValue!: number;
  private _maxScalarValue!: number;

  public get scalarResults() {
    return this._c3Service.scalarResults;
  }
  public get scalarResultsWithNone() {
    return this._c3Service.scalarResultsWithNone;
  }
  public get vectorResultsWithNone() {
    return this._c3Service.vectorResultsWithNone;
  }
  public get hasIsoObject() {
    return this._isoObject !== undefined;
  }
  public get minScalarValue() {
    return this._minScalarValue;
  }
  public get maxScalarValue() {
    return this._maxScalarValue;
  }
  public get isoValue() {
    return (<cee.ug.Isosurface>this._isoObject).isoValue;
  }
  public get minIsoValue() {
    return (<cee.ug.Isovolume>this._isoObject).minimumIsoValue;
  }
  public get maxIsoValue() {
    return (<cee.ug.Isovolume>this._isoObject).maximumIsoValue;
  }
  public get isoScalarResultId() {
    return this._isoObject.isoScalarResultId;
  }
  public get mapScalarResultId() {
    return this._isoObject.mapScalarResultId;
  }
  public get mapVectorResultId() {
    return this._isoObject.mapVectorResultId;
  }
  public get opacity() {
    return this._isoObject.opacity;
  }
  public get isIsosurfaceEditor() {
    return this.type === IsoType.Isosurface;
  }

  public constructor(c3Service: C3Service) {
    super();
    this._c3Service = c3Service;
  }

  public ngOnInit() {
    if (this.isIsosurfaceEditor) {
      this.title = 'Isosurfaces';
      this.getIso = (id) => this._c3Service._model['0'].getIsosurfaceById(id);
      this.getIsos = () =>
        this._c3Service._model['0'].getIsosurfaceArray().map(
          (iso: { id: 0 }) =>
            ({
              id: iso.id,
              name: 'Isosurface ' + iso.id,
            } as cee.ug.ResultInfo)
        );
      this.addIso = () => this._c3Service._model['0'].addIsosurface().id;
      this.deleteIso = (id) => this._c3Service._model['0'].deleteIsosurfaceById(id);
      this.setDefaultIsoValues = () => {
        (<cee.ug.Isosurface>this._isoObject).isoValue = (this._minScalarValue + this._maxScalarValue) / 2;
      };
    } else {
      this.title = 'Isovolumes';
      this.getIso = (id) => this._c3Service._model['0'].getIsovolumeById(id);
      this.getIsos = () =>
        this._c3Service._model['0']
          .getIsovolumeArray()
          .map((iso: { id: 0 }) => ({ id: iso.id, name: 'Isovolume ' + iso.id } as cee.ug.ResultInfo));
      this.addIso = () => this._c3Service._model['0'].addIsovolume().id;
      this.deleteIso = (id) => this._c3Service._model['0'].deleteIsovolumeById(id);
      this.setDefaultIsoValues = () => {
        (<cee.ug.Isovolume>this._isoObject).minimumIsoValue = (this._minScalarValue + this._maxScalarValue) / 2;
        (<cee.ug.Isovolume>this._isoObject).maximumIsoValue = this._maxScalarValue;
      };
    }

    this._c3Service.addResultRangeChangedHandler(() => {
      this.updateMinAndMaxIsoValues();
    });
  }

  public isoIdChanged(id: number) {
    this._isoObject = id !== undefined ? this.getIso(id) : (null as any);
    this.updateMinAndMaxIsoValues();
  }

  public isoValueChanged(value: number) {
    (<cee.ug.Isosurface>this._isoObject).isoValue = value;
  }

  public minIsoValueChanged(value: number) {
    (<cee.ug.Isovolume>this._isoObject).minimumIsoValue = value;
  }

  public maxIsoValueChanged(value: number) {
    (<cee.ug.Isovolume>this._isoObject).maximumIsoValue = value;
  }

  public isoScalarResultIdChanged(value: string) {
    this._isoObject.isoScalarResultId = parseInt(value);
    this.updateMinAndMaxIsoValues();
  }

  public mapScalarResultIdChanged(value: string) {
    this._isoObject.mapScalarResultId = parseInt(value);
  }

  public mapVectorResultIdChanged(value: string) {
    this._isoObject.mapVectorResultId = parseInt(value);
  }

  public opacityChanged(value: number) {
    this._isoObject.opacity = value;
  }

  private updateMinAndMaxIsoValues() {
    if (this._isoObject) {
      this._minScalarValue = this._c3Service._model['0'].getScalarResultMinimumValue(this._isoObject.isoScalarResultId);
      this._maxScalarValue = this._c3Service._model['0'].getScalarResultMaximumValue(this._isoObject.isoScalarResultId);

      // this.sharedService.changeMinIso(this._minScalarValue);
      // this.sharedService.changeMaxIso(this._maxScalarValue);

      // this.sharedService.changeCurrentMinIso((this._minScalarValue + this._maxScalarValue) / 2);
      // this.sharedService.changeCurrentMaxIso(this._maxScalarValue);

      if (!isNaN(this._minScalarValue) && !isNaN(this._maxScalarValue)) {
        this.setDefaultIsoValues();
      }
    }
  }
}
